import React, { useEffect, useState } from 'react';
import { DrModelTestingService } from '../../../services/DrModelTestingService';
import './ReviewsList.css';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';

interface GpaiReview {
  id: number;
  test_end_date: string;
  initial_diagnosis: string;
  initial_feedback: string;
  triage_approval: boolean;
  meds_recommendation: string;
  created_at: string;
  qa_json: string;
  doctor_id: string;
  test_start_date: string;
  initial_approval: boolean;
  triage: string;
  triage_feedback: string;
  model_version: string;
}

const ITEMS_PER_PAGE = 10;

function getDuration(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffMs = endDate.getTime() - startDate.getTime();
  if (isNaN(diffMs) || diffMs < 0) return '-';
  const diffSec = Math.floor(diffMs / 1000);
  const h = Math.floor(diffSec / 3600);
  const m = Math.floor((diffSec % 3600) / 60);
  const s = diffSec % 60;
  return `${h}h ${m}m ${s}s`;
}

const TABLE_FIELDS: Array<{ key: keyof GpaiReview, label: string }> = [
  { key: 'doctor_id', label: 'Doctor ID' },
  { key: 'test_start_date', label: 'Test Start Date' },
  { key: 'test_end_date', label: 'Test End Date' },
  { key: 'created_at', label: 'Created At' },
  { key: 'test_start_date', label: 'Test Duration' }, // Will use getDuration
  { key: 'initial_diagnosis', label: 'Initial Diagnosis' },
  { key: 'initial_approval', label: 'Initial Approval' },
  { key: 'initial_feedback', label: 'Initial Feedback' },
  { key: 'triage', label: 'Triage' },
  { key: 'triage_approval', label: 'Triage Approval' },
  { key: 'triage_feedback', label: 'Triage Feedback' },
  { key: 'meds_recommendation', label: 'Meds Recommendation' },
  { key: 'model_version', label: 'Model Version' },
  { key: 'qa_json', label: 'QA JSON' },
];

const GpaiReviewsList: React.FC = () => {
  const [reviews, setReviews] = useState<GpaiReview[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedReview, setSelectedReview] = useState<GpaiReview | null>(null);
  const [filter, setFilter] = useState<{ initial_approval: string, triage_approval: string }>({ initial_approval: 'all', triage_approval: 'all' });

  useEffect(() => {
    setLoading(true);
    DrModelTestingService.getAllGpaiReviews((page - 1) * ITEMS_PER_PAGE, ITEMS_PER_PAGE)
      .then((data) => {
        setReviews(data.items || data);
        setTotal(data.total || (data.items ? data.items.length : data.length));
      })
      .finally(() => setLoading(false));
  }, [page]);

  // Filtering logic
  const filteredReviews = reviews.filter((review) => {
    const initialMatch = filter.initial_approval === 'all' || String(review.initial_approval) === filter.initial_approval;
    const triageMatch = filter.triage_approval === 'all' || String(review.triage_approval) === filter.triage_approval;
    return initialMatch && triageMatch;
  });

  return (
    <div className="reviews-table-outer">
      <div className="reviews-table-header">
        <h2>GPAI Reviews</h2>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140, background: '#f7f7fa', borderRadius: 1 }}>
            <InputLabel>Initial Approval</InputLabel>
            <Select
              value={filter.initial_approval}
              label="Initial Approval"
              onChange={e => setFilter(f => ({ ...f, initial_approval: e.target.value }))}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140, background: '#f7f7fa', borderRadius: 1 }}>
            <InputLabel>Triage Approval</InputLabel>
            <Select
              value={filter.triage_approval}
              label="Triage Approval"
              onChange={e => setFilter(f => ({ ...f, triage_approval: e.target.value }))}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </div>
      <div className="reviews-table-scroll">
        <table className="reviews-table">
          <thead>
            <tr>
              {TABLE_FIELDS.map((f, idx) => {
                let thStyle = {};
                if (f.key === 'meds_recommendation') {
                  thStyle = { minWidth: 150, maxWidth: 200, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' };
                } else if (f.key === 'model_version') {
                  thStyle = { minWidth: 120, maxWidth: 160, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' };
                }
                return (
                  <th key={f.key + idx} style={thStyle}>{f.label}</th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={TABLE_FIELDS.length}>Loading...</td></tr>
            ) : filteredReviews.length === 0 ? (
              <tr><td colSpan={TABLE_FIELDS.length}>No reviews found.</td></tr>
            ) : (
              filteredReviews.map((review) => (
                <tr key={review.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedReview(review)}>
                  <td>{review.doctor_id}</td>
                  <td title={new Date(review.test_start_date).toLocaleString()} style={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{new Date(review.test_start_date).toLocaleDateString()}</td>
                  <td title={new Date(review.test_end_date).toLocaleString()} style={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{new Date(review.test_end_date).toLocaleDateString()}</td>
                  <td title={new Date(review.created_at).toLocaleString()} style={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{new Date(review.created_at).toLocaleDateString()}</td>
                  <td className="reviews-duration">{getDuration(review.test_start_date, review.test_end_date)}</td>
                  <td style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={review.initial_diagnosis}>{review.initial_diagnosis}</td>
                  <td>{review.initial_approval ? 'Yes' : 'No'}</td>
                  <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={review.initial_feedback}>{review.initial_feedback}</td>
                  <td>{review.triage}</td>
                  <td>{review.triage_approval ? 'Yes' : 'No'}</td>
                  <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={review.triage_feedback}>{review.triage_feedback}</td>
                  <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={review.meds_recommendation}>{review.meds_recommendation}</td>
                  <td>{review.model_version}</td>
                  <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={review.qa_json}>{review.qa_json}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="reviews-pagination">
        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Previous</button>
        <span style={{ margin: '0 12px' }}>Page {page}</span>
        <button onClick={() => setPage((p) => (p * ITEMS_PER_PAGE < total ? p + 1 : p))} disabled={page * ITEMS_PER_PAGE >= total}>Next</button>
      </div>
      <Dialog
        open={!!selectedReview}
        onClose={() => setSelectedReview(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            overflowY: 'auto',
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(80,112,255,0.18)',
            p: 2,
            background: 'linear-gradient(120deg, #f8fafc 70%, #e0e7ef 100%)',
            border: '2px solid #e0e7ef',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: 24, color: '#0097B2', borderBottom: '2px solid #e0e7ef', background: 'linear-gradient(90deg,#f7faff 70%,#e0e7ef 100%)', borderRadius: '18px 18px 0 0', letterSpacing: 0.5 }}>
          Review Details
        </DialogTitle>
        <DialogContent dividers sx={{ p: 4, background: '#fff', maxHeight: '60vh', overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: '#e0e7ef #fafbff', borderRadius: 2 }}>
          {selectedReview && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 4 }}>
              <Box sx={{ gridColumn: '1 / -1', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, color: '#0097B2', mb: 2, fontSize: 22, letterSpacing: 0.5, textTransform: 'uppercase' }}>General Info</Typography>
                <Typography variant="body2" sx={{ mb: 1.5, wordBreak: 'break-all', whiteSpace: 'pre-wrap', color: '#23244A' }}><b>Doctor ID:</b> {selectedReview.doctor_id}</Typography>
                <Typography variant="body2" sx={{ mb: 1.5, wordBreak: 'break-all', whiteSpace: 'pre-wrap', color: '#23244A' }}><b>Model Version:</b> {selectedReview.model_version}</Typography>
                <Typography variant="body2" sx={{ mb: 1.5, wordBreak: 'break-all', whiteSpace: 'pre-wrap', color: '#23244A' }}><b>Created At:</b> {new Date(selectedReview.created_at).toLocaleString()}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0097B2', mb: 1, fontSize: 18, letterSpacing: 0.2 }}>Test Info</Typography>
                <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all', whiteSpace: 'pre-wrap', color: '#23244A' }}><b>Test Start:</b> {new Date(selectedReview.test_start_date).toLocaleString()}</Typography>
                <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all', whiteSpace: 'pre-wrap', color: '#23244A' }}><b>Test End:</b> {new Date(selectedReview.test_end_date).toLocaleString()}</Typography>
                <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all', whiteSpace: 'pre-wrap', color: '#23244A' }}><b>Duration:</b> {getDuration(selectedReview.test_start_date, selectedReview.test_end_date)}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0097B2', mb: 1, fontSize: 18, letterSpacing: 0.2 }}>Initial Review</Typography>
                <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all', whiteSpace: 'pre-wrap', color: '#23244A' }}><b>Diagnosis:</b> {selectedReview.initial_diagnosis}</Typography>
                <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all', whiteSpace: 'pre-wrap', color: '#23244A' }}><b>Approval:</b> {selectedReview.initial_approval ? 'Yes' : 'No'}</Typography>
                <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all', whiteSpace: 'pre-wrap', color: '#23244A' }}><b>Feedback:</b> {selectedReview.initial_feedback}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0097B2', mb: 1, fontSize: 18, letterSpacing: 0.2 }}>Triage</Typography>
                <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all', whiteSpace: 'pre-wrap', color: '#23244A' }}><b>Triage:</b> {selectedReview.triage}</Typography>
                <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all', whiteSpace: 'pre-wrap', color: '#23244A' }}><b>Approval:</b> {selectedReview.triage_approval ? 'Yes' : 'No'}</Typography>
                <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all', whiteSpace: 'pre-wrap', color: '#23244A' }}><b>Feedback:</b> {selectedReview.triage_feedback}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0097B2', mb: 1, fontSize: 18, letterSpacing: 0.2 }}>Recommendation</Typography>
                <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all', whiteSpace: 'pre-wrap', color: '#23244A' }}><b>Meds Recommendation:</b> {selectedReview.meds_recommendation}</Typography>
              </Box>
              <Box sx={{ gridColumn: '1 / -1', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0097B2', mb: 1, fontSize: 18, letterSpacing: 0.2 }}>QA JSON</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap', background: '#f7f7fa', p: 1.5, borderRadius: 2, fontSize: 15, color: '#23244A', maxHeight: 180, overflowY: 'auto', mb: 1 }}>{selectedReview.qa_json}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ background: 'linear-gradient(90deg,#f7faff 70%,#e0e7ef 100%)', borderTop: '2px solid #e0e7ef', p: 3, borderRadius: '0 0 18px 18px' }}>
          <Button onClick={() => setSelectedReview(null)} variant="contained" sx={{ minWidth: 120, fontWeight: 700, fontSize: 16, background: '#6366f1', borderRadius: 2, boxShadow: '0 2px 8px rgba(80,112,255,0.10)' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <style>{`
        .MuiDialogContent-root::-webkit-scrollbar {
          height: 8px;
          width: 8px;
          background: #fafbff;
          border-radius: 8px;
        }
        .MuiDialogContent-root::-webkit-scrollbar-thumb {
          background: #e0e7ef;
          border-radius: 8px;
          min-width: 40px;
        }
        .MuiDialogContent-root::-webkit-scrollbar-thumb:hover {
          background: #d1d5db;
        }
      `}</style>
    </div>
  );
};

export default GpaiReviewsList;
